import sortBy from 'lodash/sortBy'

import { stringToDate } from 'modules/core/lib/dates'

import { MaterializedItem, MaterializedStep, Step } from 'modules/processes/domain'

export type PartialStep = {
  id?: number | null
  name: string
  stepProgressionCondition?: string | null
  stepProgressionConditionResourceKey?: string | null
  materializedStepId?: number
  materializedItems?: MaterializedItem[]
}

interface Props {
  steps?: Step[]
  materializedSteps?: MaterializedStep[]
  materializedProcessFinalizedAt?: string | null
}

// Checks if the step was created before the materialized process was finalized.
const stepCreatedBeforeMaterializedProcessFinalized = (
  createdAt?: string,
  materializedProcessFinalizedAt?: string | null
) => {
  if (!materializedProcessFinalizedAt || !createdAt) return true

  const stepCreatedAt = stringToDate(createdAt, 'backend_datetime')
  const processFinalizedAt = stringToDate(materializedProcessFinalizedAt, 'backend_datetime')
  return stepCreatedAt.isBefore(processFinalizedAt)
}

const useBuildStepList = ({ steps, materializedSteps, materializedProcessFinalizedAt }: Props) => {
  if (!steps || !materializedSteps) return { sortedSteps: [], timeLineNodes: [] }

  const getSortedSteps = () => {
    const sortedMaterializedSteps = sortBy(materializedSteps, (mStep) => {
      return steps.find((step) => mStep.stepId === step.id)?.position
    })

    const sortedSteps: PartialStep[] = sortedMaterializedSteps.map((mStep) => ({
      id: mStep.stepId,
      name: mStep.name,
      stepProgressionCondition: mStep.materializationSnapshot?.stepProgressionCondition,
      materializedStepId: mStep.id,
      materializedItems: mStep.materializedItems,
    }))

    // As steps can be actived in any order, we're to ensure that the step
    // is visible on the correct position.
    //
    // Otherwise, we would always have materialized at first, and not materialized at last
    const insertOnStepPosition = (step: Step) => {
      sortedSteps.splice(Number(step.position) - 1, 0, {
        id: step.id,
        name: step.name,
        stepProgressionCondition: step.progressionCondition,
        stepProgressionConditionResourceKey: step.progressionConditionResourceKey,
      })
    }

    sortBy(steps, (step) => Number(step.position)).forEach((step) => {
      const mStep = materializedSteps.find((mStep) => mStep.stepId === step.id)

      // If the step was created before the materialized process was finalized and
      // no corresponding materialized step exists, add it to the sortedSteps array.
      // The intention is to Skip steps created after the workflow instance has been finalized
      // because we don't want to display future steps for a finalized workflow instance
      if (
        stepCreatedBeforeMaterializedProcessFinalized(
          step.createdAt,
          materializedProcessFinalizedAt
        ) &&
        !mStep
      ) {
        insertOnStepPosition(step)
      }
    })

    return sortedSteps
  }

  const sortedSteps = getSortedSteps()
  const timeLineNodes = sortedSteps.map((step) => step.name)

  return {
    sortedSteps,
    timeLineNodes: timeLineNodes,
  }
}

export default useBuildStepList
